import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Modal, IconButton, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import "./AgentInfoModal.scss";

import { fetchAgentById } from "../../store/agentSlice";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { Agents } from "../../types/master";

// Assets
import appImg from "../../assets/images/ic_appLogo.svg";
import img1 from "../../assets/images/Login-Desktop.svg";
import img2 from "../../assets/images/List option.svg";
import img3 from "../../assets/images/Generated-answers.svg";
import LaunchImg from "../../assets/images/RocketLaunch.svg";
import VideoImg from "../../assets/images/Video.svg";
import releaseIcon from "../../assets/images/CalendarBlank.svg";
import versionIcon from "../../assets/images/GitFork.svg";
import updateIcon from "../../assets/images/ArrowsClockwise.svg";

import { formatDate } from '../../utils/helper';

// Static fallback data for screenshots (can be replaced with dynamic later)
const staticScreenshots = [img1, img2, img3];

interface AgentInfoModalProps {
    open: boolean;
    onClose: () => void;
    agentId: string | number | null;
    onAgentClick: (agentId: number) => void;
}

// Only treat absolute http(s) links as openable - a bare filename or relative
// path would otherwise resolve against this app's own origin and 404.
const isAbsoluteUrl = (value?: string | null): value is string =>
    !!value && /^https?:\/\//i.test(value.trim());

// Pill button shown for every agent for a consistent layout; greyed out and
// inert when the agent has no (valid) link for it.
const DocLink: React.FC<{ label: string; url?: string | null }> = ({ label, url }) => {
    const href = isAbsoluteUrl(url) ? url : null;
    return (
        <div
            className={`doc-link${href ? "" : " disabled"}`}
            role="button"
            tabIndex={href ? 0 : -1}
            aria-disabled={!href}
            title={href ? undefined : "Not available for this agent"}
            onClick={() => href && window.open(href, "_blank", "noopener,noreferrer")}
        >
            <span>{label}</span>
            <OpenInNewIcon fontSize="inherit" />
        </div>
    );
};

const AgentInfoModal: React.FC<AgentInfoModalProps> = ({ open, onClose, agentId, onAgentClick }) => {
    const dispatch = useAppDispatch();
    const selectedAgent = useSelector((state: RootState) => state.agents.currentAgent);
    const loading = useSelector((state: RootState) => state.agents.currentAgentLoading);
    const error = useSelector((state: RootState) => state.agents.currentAgentError);

    useEffect(() => {
        if (!agentId) return;
        dispatch(fetchAgentById(agentId));
    }, [dispatch, agentId]);

    const handleAgentLaunch = () => {
        if (selectedAgent) {
            onAgentClick(selectedAgent.id);
        }
    };

    // agent_intro_video_path is just a filename in storage, not a full URL -
    // it needs to be resolved against the blob host before it's playable.
    const getVideoUrl = useCallback((agent: Agents): string | null => {
        if (!agent.agent_intro_video_path) return null;
        return `${import.meta.env.VITE_TEMPLATE_BLOB_URL}/intro-videos/${agent.agent_intro_video_path}?${import.meta.env.VITE_TEMPLATE_G42_FILES}`;
    }, []);

    const [showVideoDemo, setShowVideoDemo] = useState(false);
    const closeVideoDemo = () => setShowVideoDemo(false);

    const videoUrl = useMemo(
        () => (selectedAgent ? getVideoUrl(selectedAgent) : null),
        [selectedAgent, getVideoUrl],
    );

    return (
        <>
        <Modal open={open} onClose={onClose}>
            <Box className="agent-info-modal">
                <IconButton className="close-icon" onClick={onClose}>
                    <CloseIcon />
                </IconButton>

                <div className="modal-content">
                    {loading && (
                        <div className="loader-container">
                            <CircularProgress size={50} />
                            <p>Loading agent information...</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="error-container">
                            <p>Error: {error}</p>
                            <button onClick={() => agentId && dispatch(fetchAgentById(agentId))}>
                                Retry
                            </button>
                        </div>
                    )}

                    {!loading && !error && selectedAgent && (
                        <>
                            {/* Header with logo and action buttons */}
                            <div className="modal-header">
                                <img src={appImg} alt="App logo" className="app-logo" />
                                <div className="action-buttons">
                                    <button className="btn btn-launch" onClick={handleAgentLaunch}>
                                        Launch <img src={LaunchImg} alt="" />
                                    </button>
                                    <button
                                        className="btn btn-video"
                                        onClick={() => setShowVideoDemo(true)}
                                        disabled={!videoUrl}
                                        title={videoUrl ? undefined : "Not available for this agent"}
                                    >
                                        Video Demo <img src={VideoImg} alt="" />
                                    </button>
                                    <DocLink label="Brochure" url={selectedAgent.brochure_link} />
                                    
                                    <DocLink label="Presentation" url={selectedAgent.presentation_link} />
                                    <DocLink label="Resources" url={selectedAgent.resources_link} />
                                </div>
                            </div>

                            <h1 className="title">{selectedAgent.agent_name}</h1>

                            <div className="tags">
                                {selectedAgent.tags?.map((tag, idx) => (
                                    <button key={idx}>{tag}</button>
                                ))}
                            </div>

                            <div className="meta">
                                <div className="meta-item">
                                    <img src={releaseIcon} alt="" className="icon" />
                                    <div>
                                        <h6>Released</h6>
                                        <span>
                                            {selectedAgent.last_updated ?? selectedAgent.created_date ?
                                                formatDate(selectedAgent.last_updated ?? selectedAgent.created_date) :
                                                'DD/MM/YY'
                                            }
                                        </span>
                                    </div>
                                </div>
                                <div className="meta-item">
                                    <img src={versionIcon} alt="" className="icon" />
                                    <div>
                                        <h6>Version</h6>
                                        <span>{selectedAgent.app_version || '1.0'}</span>
                                    </div>
                                </div>
                                <div className="meta-item">
                                    <img src={updateIcon} alt="" className="icon" />
                                    <div>
                                        <h6>Last Updated</h6>
                                        <span>{formatDate(selectedAgent.last_updated) || 'DD/MM/YY'}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="divider"></div>

                            {Array.isArray(selectedAgent?.agent_photo) && selectedAgent?.agent_photo?.length > 0 && (
                                <>
                                    <h3>App Screenshots</h3>
                                    <div className="screenshots">
                                        {selectedAgent?.agent_photo.map((img, idx) => (
                                            <img key={idx} src={img} alt="screenshot" />
                                        ))}
                                    </div>
                                </>
                            )}

                            <h3>About this agent</h3>
                            <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                                {selectedAgent.short_description ?? 'No description found.'}
                            </ReactMarkdown>

                            <h3>Why choose {selectedAgent.agent_name}</h3>
                            <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                                {selectedAgent.instructions_set || 'No information found.'}
                            </ReactMarkdown>

                            <h3>Who can use</h3>
                            <ReactMarkdown remarkPlugins={[remarkBreaks]}>
                                {selectedAgent.how_to_use ?? 'No information found.'}
                            </ReactMarkdown>
                        </>
                    )}
                </div>
            </Box>
        </Modal>

        <Modal
            open={showVideoDemo}
            onClose={closeVideoDemo}
            className="agent-info-video-modal"
            slotProps={{
                backdrop: {
                    sx: { backgroundColor: "rgba(0, 0, 0, 0.6)" },
                },
            }}
        >
            <div className="agent-info-video-modal-content">
                <button
                    className="agent-info-video-modal-close"
                    onClick={(e) => {
                        e.stopPropagation();
                        closeVideoDemo();
                    }}
                    aria-label="Close video demo"
                >
                    <CloseIcon fontSize="small" />
                </button>
                {showVideoDemo && videoUrl && (
                    <video
                        src={videoUrl}
                        controls
                        autoPlay
                        className="agent-info-video-modal-player"
                    />
                )}
            </div>
        </Modal>
        </>
    );
};

export default AgentInfoModal;